import React, { useState } from 'react';

import styled from 'styled-components';
import { get, inRange } from 'lodash';
import { Show } from '../../show';

import GridContainer from '../../layouts/components/grid-container';
import FluidContainer from '../../layouts/components/fluid-container';

import ErrorsCounter from './counter';
import PopUp from './popup';
import { PublicTitle, Attachments } from '../components';
import AudioPlayer from '../../audio-player';
import ImageGallery from '../../image-gallery';
import SharedText from '../../shared-text';
import YouTubePlayer from '../../video-player/youtube';
import TextHighlighter, { HighlightType } from '../../../components/text-highlighter';
import { exerciseDefaultProps, exercisePropTypes } from '../../../constants/exercise';
import { useScrollPosition, useWindowSize } from '../../../lib/hooks';
import Geogebra from '../../geogebra';
import HintBox from '../../hint-box';
import { getDeviceType } from '../../../lib/utils';
import Stack from '../../stack';
import MathContent from '../../math-content';
import { osName, isMobile } from 'react-device-detect';

const QuestionContainer = styled.div`
    line-height: 2.25rem;
`;

const ErrorCounterWrapper = styled.div`
    margin-bottom: calc(var(--space-unit) * 4);
`;

const PopupWrapper = styled.div`
    @keyframes fadeIn {
        0% {
            opacity: 0;
        }

        100% {
            opacity: 1;
        }
    }

    position: absolute;
    animation: fadeIn 100ms ease-in;
    transition: all 100ms linear;
`;

export const getHightlightType = (exercise, numAnswers, answer, correctionStatus) => {
    const { wrongWords } = exercise;

    if (correctionStatus) {
        return wrongWords.some((word) => word.start === answer.start && word.end === answer.end)
            ? HighlightType.CORRECT
            : HighlightType.WRONG;
    }

    if (numAnswers > wrongWords.length) {
        return HighlightType.WARNING;
    }

    return HighlightType.SELECTED;
};

let timerFunction;
const FindError = ({
    exercise,
    answers = exerciseDefaultProps.answers,
    onAnswer,
    correctionStatus,
    onPlayVideo,
    videos,
    show,
    hasAttachments = exerciseDefaultProps.hasAttachments,
    hintBoxIsOpen,
    toggleHintBox,
}) => {
    const windowSize = useWindowSize();
    const { y: scrollY } = useScrollPosition();
    const isAppleMobile = (osName === 'iOS' || osName === 'Mac OS') && isMobile;

    const [popUpInfo, setPopUpInfo] = useState({
        visible: false,
        top: 0,
        left: 0,
    });

    const showGrid = (hasAttachments.images || hasAttachments.video) && show.attachments;

    const adjustPositions = (positionXY) => {
        let newX = Math.max(32, (positionXY.left + positionXY.right) / 2 - 32);
        newX = Math.min(newX, windowSize.innerWidth - 96); // 64px di larghezza + 32px di margine

        return {
            x: newX,
            y: positionXY.top + Math.max(32, positionXY.height) + scrollY,
        };
    };

    const onTextClick = (isHightlighted, positionXY, index) => {
        const adjustedPositions = adjustPositions(positionXY);
        setPopUpInfo({
            ...popUpInfo,
            visible: isHightlighted,
            top: adjustedPositions.y,
            left: adjustedPositions.x,
            index,
            enableSelection: !isHightlighted,
        });
    };

    const onSelectionChange = (isHightlighted, positionXY, index) => {
        const adjustedPositions = adjustPositions(positionXY);
        const newPopUpInfo = {
            ...popUpInfo,
            visible: false,
            top: adjustedPositions.y,
            left: adjustedPositions.x,
            index,
            enableSelection: !isHightlighted,
        };
        setPopUpInfo(newPopUpInfo);
        clearTimeout(timerFunction);
        timerFunction = setTimeout(() => {
            setPopUpInfo({
                ...newPopUpInfo,
                visible: true,
            });
        }, 300);
    };

    const onHighlight = () => {
        if (document.getSelection()) {
            let start = parseInt(document.getSelection().getRangeAt(0).startContainer.parentNode.dataset.position);
            let end = parseInt(document.getSelection().getRangeAt(0).endContainer.parentNode.dataset.position);

            const selectedString = document.getSelection().toString();
            const firstChar = document.querySelector(`[data-position="${start}"]`).textContent;
            const lastChar = document.querySelector(`[data-position="${end}"]`).textContent;

            if (selectedString.startsWith(' ') || firstChar === ' ') {
                start++;
            }

            if (selectedString.endsWith(' ') || lastChar === ' ') {
                end--;
            }

            const filterdSelections = answers.filter((selection) => {
                if (selection.start >= start && selection.end <= end) {
                    return false;
                }

                if (selection.start >= start && selection.end > end && end >= selection.start) {
                    end = selection.end;
                    return false;
                }

                if (selection.start < start && selection.end <= end && start <= selection.end) {
                    start = selection.start;
                    return false;
                }
                return true;
            });

            onAnswer(exercise.id, [...filterdSelections, { start, end }]);

            setPopUpInfo({ visible: false });

            document.getSelection().empty();
        }
    };

    const onDeleteHighlight = () => {
        let newSelections = answers.filter((selection) => {
            return !inRange(popUpInfo.index, selection.start, selection.end + 1);
        });

        onAnswer(exercise.id, newSelections);

        setPopUpInfo({ visible: false });
    };

    const onSelectionOutside = () => {
        clearTimeout(timerFunction);
        setPopUpInfo({ visible: false });
    };

    const hintBoxLabel =
        getDeviceType() === 'DESKTOP'
            ? `Clicca e trascina per selezionare la parte di testo da evidenziare. Per eliminare la selezione clicca di nuovo sulla parte evidenziata.`
            : `Tocca e tieni premuto per selezionare la parte di testo da evidenziare. Per eliminare la selezione tocca di nuovo sulla parte evidenziata.`;

    return (
        <>
            <Show when={show.title && exercise.publicTitle}>
                <FluidContainer paddingBottom={24}>
                    <PublicTitle>{exercise.publicTitle}</PublicTitle>
                </FluidContainer>
            </Show>

            <Stack direction="vertical" gap="small">
                <Show when={show.prompt && exercise.prompt}>
                    <FluidContainer>
                        <MathContent>{exercise.prompt}</MathContent>
                    </FluidContainer>
                </Show>

                <Show when={show.hintBox}>
                    <HintBox label={hintBoxLabel} isOpen={hintBoxIsOpen} exerciseType={exercise.typeLabel} toggleHintBox={toggleHintBox} />
                </Show>
            </Stack>

            <Show when={show.attachments && hasAttachments.geogebra}>
                <FluidContainer paddingBottom={24}>
                    <Geogebra exerciseId={exercise.id} src={exercise.attachments.geogebra?.src} />
                </FluidContainer>
            </Show>

            <Show when={show.sharedText && hasAttachments.sharedText}>
                <FluidContainer paddingBottom={24} flexWrap="wrap">
                    <SharedText exercise={exercise} />
                </FluidContainer>
            </Show>

            {show.attachments && hasAttachments.audio && (
                <FluidContainer paddingBottom={24}>
                    <AudioPlayer source={exercise.attachments.audio?.src} />
                </FluidContainer>
            )}

            <GridContainer columns={[1, showGrid ? 2 : 1]} paddingBottom={24}>
                <Attachments show={showGrid}>
                    <Stack gap="desktop">
                        {hasAttachments.video && (
                            <YouTubePlayer
                                videoIdOrSource={exercise.attachments.video?.src}
                                onPlayVideo={() => onPlayVideo(exercise.id)}
                                showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                            />
                        )}
                        {hasAttachments.images && <ImageGallery images={get(exercise, 'attachments.images')} />}
                    </Stack>
                </Attachments>
                <QuestionContainer>
                    <ErrorCounterWrapper>
                        <ErrorsCounter selectedErrors={answers.length} totalErrors={exercise.options.length} />
                    </ErrorCounterWrapper>

                    <TextHighlighter
                        onSelectionChange={onSelectionChange}
                        onSelectionOutside={onSelectionOutside}
                        onTextClick={onTextClick}
                        question={exercise.plainQuestionWrong}
                        highlights={answers.map((answer) => ({
                            ...answer,
                            type: getHightlightType(exercise, answers.length, answer, correctionStatus),
                        }))}
                        disabled={!!correctionStatus}
                    />

                    {popUpInfo.visible && (
                        <PopupWrapper style={{ top: popUpInfo.top + 5, left: popUpInfo.left }}>
                            <PopUp
                                canHighlight={popUpInfo.enableSelection}
                                canRemoveHightlight={!popUpInfo.enableSelection}
                                onHighlight={onHighlight}
                                onRemoveHighlight={onDeleteHighlight}
                                isAppleMobile={isAppleMobile}
                            />
                        </PopupWrapper>
                    )}
                </QuestionContainer>
            </GridContainer>
        </>
    );
};

FindError.propTypes = exercisePropTypes;

export default FindError;
